// App.js
import { Provider } from "react-redux";
import { DataClientProvider } from "@/services/data-client/DataClientContext";
import { ApolloProvider } from "@apollo/client";
import { GqlClient } from "@/services/graph-ql/configs";
import NProgress from "nprogress";
import "nprogress/nprogress.css";
import Router from "next/router";
import CRMLayout from "@/modules/crm/crm-layout";
import "@/styles/globals.css";
import "@/styles/main.scss";
import "intro.js/introjs.css";
import "@fortawesome/fontawesome-svg-core/styles.css";
// global css imports
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "@inovua/reactdatagrid-community/index.css";
import "react-quill/dist/quill.snow.css";
import "quill-emoji/dist/quill-emoji.css";
import "quill-mention/dist/quill.mention.css";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import "reactflow/dist/style.css";
import "flag-icons/css/flag-icons.min.css";
// ----------------
import { config } from "@fortawesome/fontawesome-svg-core";
import envConfig from "@/config.js";
import store from "@/store";
import RouteGuard from "@/routes/route-guard";
import { ToastContainer, Zoom } from "react-toastify";
import { useEffect } from "react";
import { setCountriesToState } from "@/store/slices/general/countriesSlice";
import IdleTimerWrapper from "@/hooks/idleTimer";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Filler,
  TimeScale,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { ThemePalettes } from "@/services/theme-palettes";
import trackingService from '@/services/tracking/tracking-service';
import storage from "@/services/storage";
import { Enums, getObjectKeyByValue } from "@/services/dropdown-enums";
import { getDeviceInfo } from "@/services/tracking/utils/device-info";
// Create a custom cache instance
const cache = createCache({ key: "custom" });

NProgress.configure({
  showSpinner: false,
  trickleSpeed: 120,
  minimum: 0.12,
});

config.autoAddCss = false;

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  TimeScale,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const App = ({ Component, pageProps }) => {
  const Layout = Component.Layout || EmptyLayout;
  const isAuthLayout = Component.isAuthLayout;
  const hideLoading = Component.hideLoading;
  // const dispatch = useDispatch()
  // Disable console logs on production
  if (!envConfig.allowConsoles) {
    console.log = function () {};
  }

  // Initialize Tracking Service
  useEffect(() => {
    trackingService.init({
      gaTrackingId: envConfig.GATrackingId,
      // mixpanelToken: envConfig.MixpanelToken
    });

    // identify user if session exists across hard reloads
    try {
      if (typeof window !== "undefined") {
        const profileStr = storage.getItem("Profile");
        if (profileStr) {
          const user = JSON.parse(profileStr);
          let CompanyRelationship = getObjectKeyByValue(
            Enums.Relationship,
            user?.CompanyRelationship,
          );
          if (user && user.UserID) {
            // Get device info for user traits
            const deviceInfo = getDeviceInfo();

            trackingService.identify(user.UserID, {
              company_id: user.CompanyID,
              company_name: user.CompanyName,
              company_key: user.CompanyKey,
              company_relation: CompanyRelationship,
              user_id: user.UserID,
              username: user.Username,
              user_email: user.Email,
              crm_user_role: user.RoleType,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              ...deviceInfo,
            });
          }
        }
      }
    } catch (e) {
      console.error("Tracking re-identify error:", e);
    }
  }, []);

  useEffect(() => {
    console.log("app country");
    // Fetch the JSON data from the file
    fetch("/mock-data/leads/country_info.json")
      .then((response) => response.json())
      .then((data) => {
        // setCountries(data);
        store.dispatch(setCountriesToState(data));
      })
      .catch((error) => {
        console.error("Error fetching countries data:", error);
      });
  }, []);

  useEffect(() => {
    const onStart = () => {
      NProgress.start();
      // Close any open MUI Menus/Selects/Modals before navigation to prevent zombie overlays.
      // Dispatch on body (an Element) — document is not an Element and MUI/React Flow
      // call target.hasAttribute(), which throws on Document.
      if (typeof document !== "undefined" && document.body) {
        try {
          document.body.dispatchEvent(
            new KeyboardEvent("keydown", {
              key: "Escape",
              code: "Escape",
              keyCode: 27,
              which: 27,
              bubbles: true,
              cancelable: true,
            }),
          );
          document.body.click();
        } catch (e) {
          // Never block navigation if overlay teardown fails
        }
      }
    };
    const onDone = () => NProgress.done();

    Router.events.on("routeChangeStart", onStart);
    Router.events.on("routeChangeComplete", onDone);
    Router.events.on("routeChangeError", onDone);

    return () => {
      Router.events.off("routeChangeStart", onStart);
      Router.events.off("routeChangeComplete", onDone);
      Router.events.off("routeChangeError", onDone);
    };
  }, []);

  useEffect(() => {
    // Public appointment pages apply theme from the calendar API.
    // Skip the app default so it does not flash before that fetch.
    const path =
      typeof window !== "undefined" ? window.location.pathname : "";
    if (
      path.includes("/book-an-appointment") ||
      path.includes("/reschedule-an-appointment") ||
      path.includes("/cancel-an-appointment")
    ) {
      return;
    }

    let defaultTheme = ThemePalettes["default_theme"];
    const root = document.documentElement;
    if (defaultTheme?.custom) {
      Object.entries(defaultTheme?.custom).forEach(([key, value]) => {
        root.style.setProperty(`--palette-${key}`, value);
      });
    }
  }, []);
  // const { store, props } = wrapper.useWrappedStore(rest);
  // const { pageProps } = props;

  return (
    <CacheProvider value={cache}>
      <ApolloProvider client={GqlClient}>
        <Provider store={store}>
          <DataClientProvider>
            {isAuthLayout ? (
              <Layout hideLoading={hideLoading}>
                <Component {...pageProps} />
              </Layout>
            ) : (
              <RouteGuard>
                <CRMLayout>
                  <Layout>
                    <IdleTimerWrapper idleTime={envConfig?.idleTimeLimit}>
                      <Component {...pageProps} />
                    </IdleTimerWrapper>
                  </Layout>
                </CRMLayout>
              </RouteGuard>
            )}
          </DataClientProvider>
        </Provider>
        <ToastContainer
          hideProgressBar={true}
          autoClose={5000}
          // theme="light"
          theme="colored"
          position="top-center"
          transition={Zoom}
          style={{
            top: "4rem",
            zIndex: "100000", // increased due to grid overlay zIndex
          }}
        />
      </ApolloProvider>
    </CacheProvider>
  );
};

const EmptyLayout = ({ children }) => <>{children}</>;

export default App;
